import { useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/structuredLogging';
import { userActivityService } from '@/services/userActivityService';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  useEffect(() => {
    logger.info('Initializing auth', { component: 'useAuth', action: 'initialize' });
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.info('Auth state changed', { 
          component: 'useAuth',
          action: 'stateChange',
          metadata: {
            event, 
            hasSession: !!session, 
            userId: session?.user?.id 
          }
        });
        
        setSession(session);
        setUser(session?.user ?? null);
        // INITIAL_SESSION can carry a persisted token while Supabase is still
        // refreshing it. Keep consumers gated until getSession has completed.
        if (initializedRef.current || event !== 'INITIAL_SESSION') {
          setLoading(false);
        }
        
        // Track login event
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            userActivityService.trackLogin(session.user.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      logger.info('Initial session check', { 
        component: 'useAuth',
        action: 'sessionCheck',
        metadata: {
          hasSession: !!session, 
          userId: session?.user?.id 
        }
      });
      
      setSession(session);
      setUser(session?.user ?? null);
      initializedRef.current = true;
      setLoading(false);
    }).catch((error) => {
      // Never leave consumers gated forever on a failed session check.
      logger.error('Initial session check failed', { component: 'useAuth', action: 'sessionCheck' }, error as Error);
      initializedRef.current = true;
      setLoading(false);
    });

    // Safety net: a session check that never settles (flaky mobile network)
    // must not keep the whole app in a loading state.
    const watchdog = setTimeout(() => {
      if (!initializedRef.current) {
        logger.warn('Session check timed out; releasing auth loading gate', {
          component: 'useAuth', action: 'sessionCheckTimeout',
        });
        initializedRef.current = true;
        setLoading(false);
      }
    }, 8000);

    return () => {
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
      setSession(null);
      setUser(null);
    } catch (error) {
      logger.error('Error signing out', { component: 'useAuth', action: 'signOut' }, error as Error);
    }
  };

  return {
    user,
    session,
    loading,
    signOut
  };
}